import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';

/**
 * EAA-1804: turns the appbar identity chip into a dropdown so logout is
 * reachable from anywhere, not just `/profile`. Today logout only exists on
 * `ProfilePage` ("Đăng xuất khỏi tất cả"), so a user working inside `/console`
 * has to navigate away first to sign out. `AppBar` renders this in place of
 * the old `<Link to="/profile">` chip; the trigger keeps the same
 * `.portal-appbar-user` / `.portal-avatar` markup so existing appbar styling
 * is unaffected.
 *
 * Dismissal follows `ConfirmButton`'s pattern exactly: outside-click and
 * Escape listeners are attached only while `open` and torn down on every
 * close/unmount, focus moves into the menu on open and returns to the
 * trigger on close.
 */
export function UserMenu() {
    const { t } = useTranslation();
    const { authenticated, claims, logout } = useAuth();
    const displayName = claims?.name ?? claims?.email ?? claims?.sub ?? '';
    const initial = displayName ? displayName.trim().charAt(0) : '?';

    const [open, setOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Move focus into the menu once it mounts (it isn't in the DOM yet on
    // the render that flips `open`, so this can't happen inline in the
    // click handler) — mirrors ConfirmButton's popover-mount effect.
    useEffect(() => {
        if (open) {
            menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }
    }, [open]);

    // Outside-click + Escape dismissal. Only attached while `open` — so
    // there is never a listener registered while the menu is closed — and
    // torn down on every close/unmount via the effect cleanup.
    useEffect(() => {
        if (!open) {
            return undefined;
        }

        function handlePointerDown(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                event.stopPropagation();
                setOpen(false);
                triggerRef.current?.focus();
            }
        }

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    function closeAndRefocusTrigger() {
        setOpen(false);
        triggerRef.current?.focus();
    }

    function handleTriggerClick() {
        setOpen((prev) => !prev);
    }

    async function handleLogout() {
        if (loggingOut) return;
        setLoggingOut(true);
        try {
            await logout();
        } finally {
            setLoggingOut(false);
            closeAndRefocusTrigger();
        }
    }

    // Arrow-key navigation between items — a bonus per the spec, not a hard
    // requirement, but easy to get right by walking the rendered menuitems.
    function handleMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
            return;
        }
        event.preventDefault();
        const items = Array.from(
            menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []
        );
        if (items.length === 0) {
            return;
        }
        const currentIndex = items.indexOf(document.activeElement as HTMLElement);
        const nextIndex =
            event.key === 'ArrowDown'
                ? (currentIndex + 1) % items.length
                : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex]?.focus();
    }

    if (!authenticated || !displayName) {
        return null;
    }

    return (
        <div className="portal-user-menu" ref={containerRef}>
            <button
                type="button"
                ref={triggerRef}
                className="portal-appbar-user"
                onClick={handleTriggerClick}
                aria-haspopup="menu"
                aria-expanded={open}
                title={t('shell.userMenu.profileTitle')}
            >
                <span className="portal-avatar" aria-hidden="true">
                    {initial}
                </span>
                <span>{displayName}</span>
            </button>
            {open && (
                <div
                    className="portal-user-menu-dropdown"
                    role="menu"
                    aria-label={displayName}
                    ref={menuRef}
                    onKeyDown={handleMenuKeyDown}
                >
                    <Link
                        to="/profile"
                        role="menuitem"
                        className="portal-user-menu-item"
                        onClick={closeAndRefocusTrigger}
                    >
                        {t('shell.userMenu.profileTitle')}
                    </Link>
                    <div className="portal-user-menu-separator" role="separator" />
                    <button
                        type="button"
                        role="menuitem"
                        className="portal-user-menu-item portal-user-menu-item-danger"
                        onClick={() => void handleLogout()}
                        disabled={loggingOut}
                    >
                        {loggingOut ? t('shell.userMenu.loggingOut') : t('shell.userMenu.logout')}
                    </button>
                </div>
            )}
        </div>
    );
}
